import { useEffect, useState, useMemo } from 'react'
import { supabase } from '../services/supabase'
import AdminLayout from '../components/AdminLayout'

interface CustomerOrder {
  id: string
  total_amount: number
  status: string
  created_at: string
}

interface Customer {
  id: string
  email: string
  full_name: string
  phone: string | null
  is_admin: boolean
  created_at: string
  updated_at: string
  orders?: CustomerOrder[]
  total_spent?: number
  order_count?: number
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [updatingCustomerId, setUpdatingCustomerId] = useState<string | null>(null)
  const [updateMessage, setUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Fetch all customers
  useEffect(() => {
    fetchCustomers()
  }, [])

  const fetchCustomers = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch all customers
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false })

      if (customersError) throw customersError

      if (!customersData || customersData.length === 0) {
        setCustomers([])
        setLoading(false)
        return
      }

      // Fetch order stats for each customer
      const customerIds = customersData.map(c => c.id)
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('id, customer_id, total_amount, status, created_at')
        .in('customer_id', customerIds)

      if (ordersError) throw ordersError

      // Combine customers with their order stats
      const customersWithStats = customersData.map(customer => {
        const customerOrders = (ordersData || []).filter(o => o.customer_id === customer.id)
        const total_spent = customerOrders.reduce((sum, o) => sum + Number(o.total_amount), 0)

        return {
          ...customer,
          orders: customerOrders,
          total_spent,
          order_count: customerOrders.length
        }
      })

      setCustomers(customersWithStats)
    } catch (err) {
      console.error('Error fetching customers:', err)
      setError('Failed to load customers. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Toggle admin status
  const toggleAdminStatus = async (customerId: string, currentIsAdmin: boolean) => {
    setUpdatingCustomerId(customerId)
    setUpdateMessage(null)

    // Store previous state for rollback
    const previousCustomers = [...customers]

    // Optimistic update
    setCustomers(prev => prev.map(customer =>
      customer.id === customerId ? { ...customer, is_admin: !currentIsAdmin } : customer
    ))

    // Also update selected customer if it's open
    if (selectedCustomer?.id === customerId) {
      setSelectedCustomer(prev => prev ? { ...prev, is_admin: !currentIsAdmin } : null)
    }

    try {
      const { error } = await supabase
        .from('customers')
        .update({ is_admin: !currentIsAdmin })
        .eq('id', customerId)

      if (error) throw error

      setUpdateMessage({ type: 'success', text: `Admin status ${!currentIsAdmin ? 'granted' : 'revoked'} successfully` })
      setTimeout(() => setUpdateMessage(null), 3000)
    } catch (err) {
      console.error('Error updating admin status:', err)
      // Revert on error
      setCustomers(previousCustomers)
      if (selectedCustomer?.id === customerId) {
        const originalCustomer = previousCustomers.find(c => c.id === customerId)
        if (originalCustomer) setSelectedCustomer(originalCustomer)
      }
      setUpdateMessage({ type: 'error', text: 'Failed to update admin status' })
      setTimeout(() => setUpdateMessage(null), 3000)
    } finally {
      setUpdatingCustomerId(null)
    }
  }

  // Filter customers
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers

    const query = searchQuery.toLowerCase().trim()
    return customers.filter(customer => {
      const nameMatch = customer.full_name?.toLowerCase().includes(query)
      const emailMatch = customer.email?.toLowerCase().includes(query)
      const phoneMatch = customer.phone?.toLowerCase().includes(query)
      return nameMatch || emailMatch || phoneMatch
    })
  }, [customers, searchQuery])

  // Helper functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const clearSearch = () => {
    setSearchQuery('')
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Customer Management</h1>
          <p className="text-gray-500 text-sm">View and manage all registered customers</p>
        </div>

        {/* Update Message */}
        {updateMessage && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            updateMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {updateMessage.text}
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400 hover:text-gray-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchCustomers}
              disabled={loading}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            {filteredCustomers.length === customers.length
              ? `${customers.length} customer${customers.length !== 1 ? 's' : ''}`
              : `${filteredCustomers.length} of ${customers.length} customers`}
          </p>
          {searchQuery.trim() && (
            <button
              onClick={clearSearch}
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Clear search
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading customers...</p>
          </div>
        ) : error ? (
          /* Error State */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-red-600">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-gray-700 mb-4">{error}</p>
            <button
              onClick={fetchCustomers}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : customers.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No customers yet</h2>
            <p className="text-gray-500">Customers will appear here once they create an account.</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          /* No Results State */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No customers found</h2>
            <p className="text-gray-500 mb-6">Try adjusting your search.</p>
            <button
              onClick={clearSearch}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Clear Search
            </button>
          </div>
        ) : (
          /* Customers Table */
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Joined</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Orders</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Spent</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                      {/* Customer Name */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-purple-600">
                              {customer.full_name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {customer.full_name || 'Unknown'}
                          </span>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm text-gray-900">{customer.email}</p>
                          {customer.phone && (
                            <p className="text-xs text-gray-500">{customer.phone}</p>
                          )}
                        </div>
                      </td>

                      {/* Joined Date */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-600">{formatDate(customer.created_at)}</span>
                      </td>

                      {/* Order Count */}
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-900">{customer.order_count || 0}</span>
                      </td>

                      {/* Total Spent */}
                      <td className="px-4 py-4">
                        <span className="text-sm font-semibold text-gray-900">
                          ${(customer.total_spent || 0).toFixed(2)}
                        </span>
                      </td>

                      {/* Role */}
                      <td className="px-4 py-4">
                        {customer.is_admin ? (
                          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                            Admin
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                            Customer
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4">
                        <button
                          onClick={() => setSelectedCustomer(customer)}
                          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Customer Detail Modal */}
        {selectedCustomer && (
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 99999 }}
            onClick={(e) => e.target === e.currentTarget && setSelectedCustomer(null)}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-semibold text-purple-600">
                      {selectedCustomer.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      {selectedCustomer.full_name || 'Unknown'}
                    </h2>
                    <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Admin Toggle */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700">Admin Access</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedCustomer.is_admin
                          ? 'This user has admin privileges'
                          : 'Grant admin privileges to this user'}
                      </p>
                    </div>
                    <button
                      onClick={() => toggleAdminStatus(selectedCustomer.id, selectedCustomer.is_admin)}
                      disabled={updatingCustomerId === selectedCustomer.id}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        selectedCustomer.is_admin
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      } ${updatingCustomerId === selectedCustomer.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {updatingCustomerId === selectedCustomer.id
                        ? 'Updating...'
                        : selectedCustomer.is_admin
                          ? 'Revoke Admin'
                          : 'Make Admin'}
                    </button>
                  </div>
                </div>

                {/* Customer Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Full Name</span>
                      <span className="text-sm font-medium text-gray-900">{selectedCustomer.full_name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Email</span>
                      <span className="text-sm font-medium text-gray-900">{selectedCustomer.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Phone</span>
                      <span className="text-sm font-medium text-gray-900">{selectedCustomer.phone || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Customer ID</span>
                      <span className="text-sm font-mono text-gray-600 text-xs">{selectedCustomer.id}</span>
                    </div>
                  </div>
                </div>

                {/* Account Stats */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Statistics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{selectedCustomer.order_count || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Total Orders</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">${(selectedCustomer.total_spent || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">Total Spent</p>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Details</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Joined</span>
                      <span className="text-sm font-medium text-gray-900">{formatDateTime(selectedCustomer.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Last Updated</span>
                      <span className="text-sm font-medium text-gray-900">{formatDateTime(selectedCustomer.updated_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Recent Orders */}
                {selectedCustomer.orders && selectedCustomer.orders.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Orders</h3>
                    <div className="space-y-2">
                      {selectedCustomer.orders.slice(0, 5).map((order) => (
                        <div key={order.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <div>
                            <p className="text-sm font-mono text-gray-900">#{order.id.substring(0, 8).toUpperCase()}</p>
                            <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">${Number(order.total_amount).toFixed(2)}</p>
                            <p className={`text-xs capitalize ${
                              order.status === 'delivered' ? 'text-green-600' :
                              order.status === 'cancelled' ? 'text-red-600' :
                              order.status === 'shipped' ? 'text-purple-600' :
                              'text-gray-500'
                            }`}>
                              {order.status}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
